import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
} from '@mui/material';
import {
  Image,
} from '@mui/icons-material';
import { HotKey } from '../types';
import apiService from '../services/api';

/** Publish countdown (#14 / #21); UI mirrors remaining time only. */
const PUBLISH_COUNTDOWN_SECONDS = 15;

const LINE_TEXT_SX = {
  fontSize: '13px',
  lineHeight: 1.35,
  color: '#333333',
  fontWeight: 400,
} as const;

interface PreviewSendModalProps {
  open: boolean;
  onClose: () => void;
  hotKey: HotKey | null;
  mode: 'start' | 'stop';
  onSuccess?: () => void;
  onEdit?: (hotKey: HotKey) => void;
}

const PreviewSendModal: React.FC<PreviewSendModalProps> = ({
  open,
  onClose,
  hotKey,
  mode,
  onSuccess,
  onEdit,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [imageError, setImageError] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(PUBLISH_COUNTDOWN_SECONDS);
  const publishStartedRef = useRef(false);

  /**
   * Reset countdown synchronously before paint so passive effects (auto-publish at 0) never see
   * stale secondsLeft from the previous session — that was firing publish immediately on reopen.
   */
  useLayoutEffect(() => {
    if (!open || !hotKey) return;
    setError('');
    setImageError(false);
    publishStartedRef.current = false;
    if (mode === 'start') {
      setSecondsLeft(PUBLISH_COUNTDOWN_SECONDS);
    }
  }, [open, hotKey, hotKey?.id, mode]);

  const runStartPublish = useCallback(async () => {
    if (!hotKey || publishStartedRef.current) return;
    publishStartedRef.current = true;
    setLoading(true);
    setError('');
    try {
      await apiService.startHotKey(hotKey.id, hotKey.keepAliveMinutes);
      onSuccess?.();
      onClose();
    } catch (err: unknown) {
      const e = err as { message?: string; status?: number; response?: { status?: number; data?: { message?: string } } };
      if (e?.message === 'Session expired.' || e?.status === 401 || e?.response?.status === 401) {
        return;
      }
      setError(e?.message || e?.response?.data?.message || 'Operation failed. Please try again.');
      publishStartedRef.current = false;
      setSecondsLeft((s) => (s === 0 ? PUBLISH_COUNTDOWN_SECONDS : s));
    } finally {
      setLoading(false);
    }
  }, [hotKey, onSuccess, onClose]);

  const runStop = useCallback(async () => {
    if (!hotKey || publishStartedRef.current) return;
    publishStartedRef.current = true;
    setLoading(true);
    setError('');
    try {
      await apiService.stopHotKey(hotKey.id);
      onSuccess?.();
      onClose();
    } catch (err: unknown) {
      const e = err as { message?: string; status?: number; response?: { status?: number; data?: { message?: string } } };
      if (e?.message === 'Session expired.' || e?.status === 401 || e?.response?.status === 401) {
        return;
      }
      setError(e?.message || e?.response?.data?.message || 'Operation failed. Please try again.');
      publishStartedRef.current = false;
    } finally {
      setLoading(false);
    }
  }, [hotKey, onSuccess, onClose]);

  useEffect(() => {
    if (!open || mode !== 'start' || loading || !hotKey) return;
    if (secondsLeft <= 0) return;
    const id = window.setTimeout(() => {
      setSecondsLeft((s) => s - 1);
    }, 1000);
    return () => window.clearTimeout(id);
  }, [open, mode, loading, hotKey, hotKey?.id, secondsLeft]);

  useEffect(() => {
    if (!open || mode !== 'start' || !hotKey || loading) return;
    if (secondsLeft > 0) return;
    if (publishStartedRef.current) return;
    void runStartPublish();
  }, [secondsLeft, open, mode, hotKey, hotKey?.id, loading, runStartPublish]);

  const handlePublishNow = () => {
    void runStartPublish();
  };

  const handleEdit = () => {
    if (!hotKey) return;
    publishStartedRef.current = true;
    onClose();
    onEdit?.(hotKey);
  };

  if (!hotKey) return null;

  const imageSrc = hotKey.messageImage && !imageError ? hotKey.messageImage : null;

  if (mode === 'stop') {
    return (
      <Dialog
        open={open}
        onClose={loading ? undefined : onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#EEEEEE',
          },
        }}
      >
        <DialogContent sx={{ px: 5, pt: 5, pb: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Typography
            component="div"
            sx={{
              fontSize: '1.25rem',
              fontWeight: 600,
              color: '#000000',
              lineHeight: 1.2,
              mb: 2,
            }}
          >
            HotQuu is On Air
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {imageSrc ? (
              <Box
                component="img"
                src={imageSrc}
                alt=""
                onError={() => setImageError(true)}
                sx={{
                  width: 56,
                  height: 56,
                  flexShrink: 0,
                  objectFit: 'cover',
                  borderRadius: 0.5,
                }}
              />
            ) : (
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  flexShrink: 0,
                  borderRadius: 0.5,
                  backgroundColor: '#f5f5f5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Image sx={{ fontSize: 28, color: '#999' }} />
              </Box>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
              <Typography component="div" sx={LINE_TEXT_SX} noWrap>
                {hotKey.rt1 || hotKey.messageText.split(' - ')[0] || hotKey.messageText}
              </Typography>
              <Typography component="div" sx={LINE_TEXT_SX} noWrap>
                {hotKey.rt2 || hotKey.messageText.split(' - ')[1] || ''}
              </Typography>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 2, pb: 2, pt: 2 }}>
          <Button
            onClick={onClose}
            disabled={loading}
            sx={{
              textTransform: 'none',
              color: '#999999',
              mr: 'auto',
              minWidth: 'auto',
              px: 0,
              '&:hover': {
                backgroundColor: 'transparent',
                textDecoration: 'underline',
                color: '#999999',
              },
            }}
          >
            Cancel
          </Button>

          <Button
            onClick={handleEdit}
            variant="contained"
            disabled={loading}
            sx={{
              textTransform: 'none',
              minWidth: 100,
              backgroundColor: '#999999',
              color: '#ffffff',
              boxShadow: 'none',
              '&:hover': { backgroundColor: '#888888', boxShadow: 'none' },
            }}
          >
            Edit
          </Button>

          <Button
            onClick={() => void runStop()}
            variant="contained"
            disabled={loading}
            sx={{
              backgroundColor: '#4DAADB',
              color: '#ffffff',
              textTransform: 'none',
              minWidth: 120,
              boxShadow: 'none',
              '&:hover': { backgroundColor: '#409dcf', boxShadow: 'none' },
            }}
          >
            {loading ? 'Stopping...' : 'Stop HotQuu'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: '#EEEEEE',
        },
      }}
    >
      <DialogContent sx={{ px: 5, pt: 5, pb: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 1 }}>
          <Typography
            component="div"
            sx={{
              fontSize: '1.5rem',
              fontWeight: 700,
              color: '#000000',
              lineHeight: 1.2,
              minWidth: 0,
              mb: 2,
            }}
          >
            {loading ? 'Publishing…' : `Publishing in ${secondsLeft}s`}
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {imageSrc ? (
              <Box
                component="img"
                src={imageSrc}
                alt=""
                onError={() => setImageError(true)}
                sx={{
                  width: 56,
                  height: 56,
                  flexShrink: 0,
                  objectFit: 'cover',
                  borderRadius: 0.5,
                }}
              />
            ) : (
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  flexShrink: 0,
                  borderRadius: 0.5,
                  backgroundColor: '#f5f5f5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Image sx={{ fontSize: 28, color: '#999' }} />
              </Box>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
              <Typography component="div" sx={LINE_TEXT_SX} noWrap>
                {hotKey.rt1 || hotKey.messageText.split(' - ')[0] || hotKey.messageText}
              </Typography>
              <Typography component="div" sx={LINE_TEXT_SX} noWrap>
                {hotKey.rt2 || hotKey.messageText.split(' - ')[1] || ''}
              </Typography>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 2, pb: 2, pt: 2 }}>
        <Button
          onClick={onClose}
          disabled={loading}
          sx={{
            textTransform: 'none',
            color: '#999999',
            mr: 'auto',
            minWidth: 'auto',
            px: 0,
            '&:hover': {
              backgroundColor: 'transparent',
              textDecoration: 'underline',
              color: '#999999',
            },
          }}
        >
          Cancel
        </Button>

        <Button
          onClick={handleEdit}
          variant="contained"
          disabled={loading}
          sx={{
            textTransform: 'none',
            minWidth: 100,
            backgroundColor: '#999999',
            color: '#ffffff',
            boxShadow: 'none',
            '&:hover': { backgroundColor: '#888888', boxShadow: 'none' },
          }}
        >
          Edit
        </Button>

        <Button
          onClick={handlePublishNow}
          variant="contained"
          disabled={loading}
          sx={{
            backgroundColor: '#4DAADB',
            color: '#ffffff',
            textTransform: 'none',
            minWidth: 120,
            boxShadow: 'none',
            '&:hover': { backgroundColor: '#409dcf', boxShadow: 'none' },
          }}
        >
          {loading ? 'Publishing...' : 'Publish Now'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PreviewSendModal;
